import Player from "@vimeo/player";

if (!customElements.get("video-snippet")) {
  class VideoComponent extends HTMLElement {
    constructor() {
      super();

      this.video = this.querySelector("video, .video");
      this.player = null;
      this._playing = false;
      this.timeout = null;
      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.handleResize = () => {
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          this.checkVideo();
        }, 200);
      };
      this.handleMotionChange = () => this.checkVideo();
      this.states = [];

      if (!this.video) return;

      this.isVimeo = "vimeo" in this.video.dataset;

      if (this.isVimeo) {
        this.vimeoSrc = this.src;
        this.player = new Player(this.video, {
          id: this.vimeoSrc,
        });
      }

      this.checkVideo();

      this.addEventListener("video-snippet:play", () => this._play());
      this.addEventListener("video-snippet:pause", () => this._pause());
    }

    connectedCallback() {
      window.addEventListener("resize", this.handleResize);
      if (this.reducedMotion?.addEventListener) {
        this.reducedMotion.addEventListener("change", this.handleMotionChange);
      } else {
        this.reducedMotion?.addListener?.(this.handleMotionChange);
      }
    }

    disconnectedCallback() {
      window.removeEventListener("resize", this.handleResize);
      if (this.reducedMotion?.removeEventListener) {
        this.reducedMotion.removeEventListener("change", this.handleMotionChange);
      } else {
        this.reducedMotion?.removeListener?.(this.handleMotionChange);
      }
    }

    checkFillDirection() {
      if (!this.isVimeo) return;

      Promise.all([
        this.player.getVideoWidth(),
        this.player.getVideoHeight(),
      ]).then((dimensions) => {
        const { width, height } = this.getBoundingClientRect();
        const [videoWidth, videoHeight] = dimensions;

        const containerAR = width / height;
        const videoAR = videoWidth / videoHeight;

        this.dataset.fillDirection =
          videoAR <= containerAR ? "horizontal" : "vertical";
      });
    }

    shouldUsePosterOnly() {
      return (
        !this.isVimeo &&
        this.video &&
        this.video.autoplay &&
        (this.reducedMotion.matches ||
          (navigator.connection && navigator.connection.saveData))
      );
    }

    checkVideo() {
      if (!this.video) return;

      if (this.isVimeo) {
        if (this.src !== this.vimeoSrc) {
          this.vimeoSrc = this.src;
          this.player.loadVideo(this.vimeoSrc).then(() => {
            this.checkFillDirection();
          });
        } else {
          this.checkFillDirection();
        }
        return;
      }

      this.updatePoster();

      if (this.shouldUsePosterOnly()) {
        this.video.pause();
        this.video.removeAttribute("src");
        this.video.load();
        return;
      }

      if (this.src && this.src !== this.video.getAttribute("src")) {
        this.video.src = this.src;
        if (this.video.autoplay) this.video.play().catch(() => {});
      }
    }

    updatePoster() {
      if (this.poster && this.video.getAttribute("poster") !== this.poster) {
        this.video.setAttribute("poster", this.poster);
      }
    }

    _play() {
      if (this._playing) return;
      if (this.isVimeo) {
        if (!this.player) return;

        this._playPromise = this.player.play().then(() => {
          this._playPromise = null;
        });
      } else {
        this._playPromise = this.video.play().then(() => {
          this._playPromise = null;
        });
      }
    }

    _pause() {
      if (this._playPromise) return;

      if (this.isVimeo) {
        if (!this.player) return;

        this.player.pause();
      } else {
        this.video.pause();
      }
    }

    get src() {
      if (!this.video) return "";
      const isMobile = window.matchMedia(
        this.dataset.mediaQuery || "(max-width: 576px)",
      );
      const src = this.video.dataset.mediaSrc;
      const mobileSrc =
        "mediaSrcMobile" in this.video.dataset
          ? this.video.dataset.mediaSrcMobile
          : src;
      return isMobile.matches ? mobileSrc : src;
    }

    get poster() {
      if (!this.video) return "";
      const isMobile = window.matchMedia(
        this.dataset.mediaQuery || "(max-width: 576px)",
      );
      const poster = this.video.dataset.mediaPoster;
      const mobilePoster =
        "mediaPosterMobile" in this.video.dataset
          ? this.video.dataset.mediaPosterMobile
          : poster;
      return isMobile.matches ? mobilePoster : poster;
    }
  }

  customElements.define("video-snippet", VideoComponent);
}


// Runtime enhancement appended in global.min.js for native video pause/play controls.

(()=>{let e=customElements.get("video-snippet");if(!e||e.prototype.__headGolfToggleEnhanced)return;e.prototype.__headGolfToggleEnhanced=!0;let t=e.prototype.connectedCallback,i=e.prototype.disconnectedCallback,o=e.prototype.checkVideo,n=e.prototype.shouldUsePosterOnly;e.prototype.shouldUsePosterOnly=function(){return n?n.call(this)&&!this.__videoToggleUserRequested:!1},e.prototype.__videoToggleInit=function(){if(this.__videoToggleReady)return;this.toggleButton=this.querySelector("[data-video-toggle]");if(!this.toggleButton||!this.video||this.isVimeo)return;this.__videoToggleReady=!0,this.__videoToggleClick=()=>this.__videoToggleToggle(),this.__videoToggleSync=()=>this.__videoToggleUpdate(),this.toggleButton.addEventListener("click",this.__videoToggleClick),this.video.addEventListener("play",this.__videoToggleSync),this.video.addEventListener("pause",this.__videoToggleSync),this.__videoToggleUpdate()},e.prototype.__videoToggleDestroy=function(){this.toggleButton&&this.__videoToggleClick&&this.toggleButton.removeEventListener("click",this.__videoToggleClick),this.video&&this.__videoToggleSync&&(this.video.removeEventListener("play",this.__videoToggleSync),this.video.removeEventListener("pause",this.__videoToggleSync)),this.__videoToggleReady=!1},e.prototype.__videoToggleToggle=function(){if(!this.video)return;if(this.video.paused||!this.video.getAttribute("src")){this.__videoToggleUserRequested=!0,this.__videoTogglePausedByUser=!1,this.updatePoster?.();let e=this.src;e&&this.video.getAttribute("src")!==e&&(this.video.src=e,this.video.load()),this.video.play().catch(()=>{}).finally(()=>this.__videoToggleUpdate())}else this.__videoTogglePausedByUser=!0,this.video.pause(),this.__videoToggleUpdate()},e.prototype.__videoToggleUpdate=function(){if(!this.toggleButton||!this.video)return;let e=!this.video.paused&&!!this.video.currentSrc,t=e?"Pause":"Play";this.toggleButton.setAttribute("aria-label",t+" video"),this.toggleButton.setAttribute("aria-pressed",String(e));let i=this.toggleButton.querySelector("[data-video-toggle-label]");i&&(i.textContent=t)},e.prototype.connectedCallback=function(){t?.call(this),this.__videoToggleInit?.()},e.prototype.disconnectedCallback=function(){this.__videoToggleDestroy?.(),i?.call(this)},e.prototype.checkVideo=function(){if(this.video&&!this.isVimeo&&this.__videoTogglePausedByUser){let e=this.video.autoplay;this.video.autoplay=!1,o.call(this),this.video.autoplay=e,this.video.paused||this.video.pause()}else o.call(this);this.__videoToggleInit?.(),this.__videoToggleUpdate?.()};let r=()=>document.querySelectorAll("video-snippet").forEach(e=>e.__videoToggleInit?.());customElements.whenDefined("video-snippet").then(r),document.readyState==="loading"?document.addEventListener("DOMContentLoaded",r,{once:!0}):r()})();
